<template>
  <div
    ref="scroller"
    class="h-full overflow-y-auto overscroll-contain"
    @scroll="handleScroll"
  >
    <div class="px-4 pb-[calc(env(safe-area-inset-bottom)+24px)]">
      <h1 class="px-2.5 pt-4 pb-3 text-[20px] leading-[26px] text-gray-800">
        <template v-if="total !== undefined">
          {{ formatNumber(total) }}
          {{ total === 1 ? 'registration' : 'registrations' }} for "{{
            store.query
          }}"
        </template>
        <template v-else>Searching…</template>
      </h1>

      <template v-if="loading">
        <SearchRowSkeleton v-for="n in 8" :key="n" />
      </template>
      <template v-else-if="items.length">
        <SearchResultRow
          v-for="item in items"
          :key="item.id"
          :thumb-type="itemThumbType(item)"
          :thumb-src="itemThumbSrc(item)"
          :name="item.name"
          :class="itemDimClass(item)"
          @select="goToItem(item)"
        >
          <template #subtitle>
            <DacrMarkIcon class="size-[14px] shrink-0 text-gray-800" />
            <span class="truncate">
              {{ formatTimestamp(item.file?.timestamp ?? item.timestamp) }}
            </span>
          </template>
        </SearchResultRow>
      </template>
      <NoSearchResults v-else />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { DacrMarkIcon } from '@dacr/icons'
import {
  useSearchItemsQuery,
  type Item,
} from '@/shared/graphql/legacy/generated'
import { useMobileSearchStore } from '@/features/search/store'
import { useSearchNavigation } from '@/features/search/composables/useSearchNavigation'
import {
  itemThumbType,
  itemThumbSrc,
  itemDimClass,
} from '@/features/search/utils'
import { formatNumber } from '@/shared/utils/number'
import { formatTimestamp } from '@/shared/utils/datetime'
import SearchResultRow from '@/features/search/components/mobile/SearchResultRow.vue'
import SearchRowSkeleton from '@/features/search/components/mobile/SearchRowSkeleton.vue'
import NoSearchResults from '@/features/search/components/mobile/NoSearchResults.vue'

const store = useMobileSearchStore()
const { goToItem } = useSearchNavigation()

const scroller = ref<HTMLElement>()

const variables = computed(() => ({ query: store.query }))

const { result, loading, fetchMore } = useSearchItemsQuery(variables)

const total = computed(() => result.value?.searchItems.total)
const items = computed<Item[]>(
  () => (result.value?.searchItems.files as Item[] | undefined) ?? []
)

const fetchNextPage = () => {
  const next = result.value?.searchItems.next
  if (!next || loading.value) return
  fetchMore({
    variables: { next },
    updateQuery: (previousData, { fetchMoreResult: newData }) => {
      if (!newData) return previousData
      return {
        __typename: 'Query' as const,
        searchItems: {
          ...previousData.searchItems,
          files: [
            ...previousData.searchItems.files,
            ...newData.searchItems.files,
          ],
          next: newData.searchItems.next,
          total: newData.searchItems.total,
        },
      }
    },
  })
}

const handleScroll = () => {
  const el = scroller.value
  if (!el) return
  if ((el.scrollTop + el.clientHeight) / el.scrollHeight >= 0.8) {
    fetchNextPage()
  }
}
</script>
